import React, { Component } from "react";
import {
  getBannerList,
  toggleBannerStatus,
  deleteBanner,
  reorderBanners,
  getBannerAnalytics,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, Btn, StatCard, StatRow, SearchPill, ChipTabs, Thumb, BarChart,
  Dialog, DialogHead, ConfirmDialog, Table,
} from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * Guest app banners — screen E4·17 of the console redesign
 * (web-banner-manager.html).
 *
 * A banner is a picture, so the design shows the picture: wide cards with the
 * artwork at full size, its slot, and what it is scheduled to do. The table it
 * replaces showed an 80×45 thumbnail and made you guess.
 *
 * Re-skin plus one wiring fix: getBannerAnalytics is now loaded on mount. The
 * previous screen already had summary tiles gated on that data but only ever
 * fetched it when the analytics modal was opened, so the tiles never appeared.
 */

// Helper to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl;
  return `${process.env.REACT_APP_DEFAULTAUTH}${imageUrl}`;
};

const CSS = `
.banner-card{background:#fff;border:1px solid ${C.tint};border-radius:18px;overflow:hidden;
  display:flex;transition:box-shadow .15s,border-color .15s}
.banner-card:hover{border-color:${C.sand};box-shadow:0 8px 22px rgba(61,40,23,.09)}
.banner-act{border:0;background:transparent;padding:0;font-family:inherit;font-size:13px;
  font-weight:500;color:${C.brown};cursor:pointer}
.banner-act:hover{text-decoration:underline}
.banner-act.is-danger{color:${C.red}}
.banner-move{width:28px;height:28px;border-radius:14px;border:1px solid ${C.border};
  background:#fff;color:${C.brown};display:grid;place-items:center;cursor:pointer;padding:0}
.banner-move:disabled{opacity:.35;cursor:default}
.banner-page{min-width:34px;height:34px;border-radius:17px;border:1px solid ${C.border};
  background:#fff;color:${C.taupe};font-size:13px;font-family:inherit;cursor:pointer;padding:0 10px}
.banner-page.is-on{background:${C.brown};border-color:transparent;color:#fff}
.banner-page:disabled{opacity:.4;cursor:default}
.banner-limit{height:42px;padding:0 34px 0 16px;border-radius:21px;background:#fff;
  border:1px solid ${C.border};color:${C.taupe};font-size:14px;font-family:inherit;cursor:pointer;
  -webkit-appearance:none;appearance:none;
  background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23a3917c'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 10px center;background-size:18px}
.banner-limit:focus{outline:none;border-color:${C.sand}}
`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;

/** LIVE / SCHEDULED / ENDED / PAUSED, from the dates the API already sends. */
const lifecycle = (b) => {
  const now = new Date();
  if (!b.isActive) return { label: "PAUSED", bg: C.tint, fg: C.taupe };
  if (b.endDate && now > new Date(b.endDate)) return { label: "ENDED", bg: "#f3ece2", fg: C.taupe };
  if (b.startDate && now < new Date(b.startDate)) return { label: "SCHEDULED", bg: "#f3ece2", fg: C.taupe };
  return { label: "LIVE", bg: "#e8f3e6", fg: "#2e7d32" };
};

/** The schedule line under a banner's title. */
const scheduleLine = (b) => {
  const from = fmtDate(b.startDate);
  const to = fmtDate(b.endDate);
  if (from && to) return `${from} – ${to}`;
  if (from) return `From ${from} · no end date`;
  if (to) return `Runs until ${to}`;
  return "Always on · no schedule set";
};

const linkLine = (b) => {
  if (b.linkType === "coupon") {
    return b.linkedCouponId && b.linkedCouponId.code
      ? `Opens coupon ${b.linkedCouponId.code}`
      : "Opens a coupon";
  }
  if (b.linkType === "external") return `Opens ${b.externalUrl || "an external link"}`;
  if (b.linkType === "internal") return `Opens ${b.internalRoute || "an app screen"}`;
  return "Not clickable";
};

class BannerList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      statusFilter: "",
      analyticsModal: false,
      analyticsData: null,
      // Search and pagination
      searchQuery: "",
      currentPage: 1,
      limit: 10,
      pagination: null,
      // Reordering
      reordering: false,
      order: [],
      savingOrder: false,
      // Modal states
      showToggleModal: false,
      showDeleteModal: false,
      selectedBannerId: null,
      selectedBannerStatus: null,
      actionLoading: false,
    };
    this.searchTimeout = null;
  }

  componentDidMount() {
    this.getAllBanners();
    this.loadAnalytics();
  }

  componentWillUnmount() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  }

  getAllBanners = () => {
    const { statusFilter, searchQuery, currentPage, limit } = this.state;
    const params = {
      page: currentPage,
      limit: limit,
    };
    if (statusFilter) params.status = statusFilter;
    if (searchQuery.trim()) params.search = searchQuery.trim();

    getBannerList(params)
      .then((result) => {
        if (result.status === 200) {
          this.setState({
            isLoaded: true,
            items: result.data,
            pagination: result.pagination,
            order: result.data.map((b) => b._id),
            reordering: false,
          });
        } else {
          toast.error(result.message || "Failed to fetch banners");
        }
      })
      .catch(() => toast.error("Error fetching banners"));
  };

  handleSearchChange = (searchQuery) => {
    this.setState({ searchQuery, currentPage: 1 });

    // Debounce search
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.getAllBanners();
    }, 500);
  };

  handleLimitChange = (e) => {
    this.setState({ limit: parseInt(e.target.value), currentPage: 1 }, () => {
      this.getAllBanners();
    });
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page }, () => {
      this.getAllBanners();
    });
  };

  setStatusFilter = (v) => {
    this.setState({ statusFilter: v, currentPage: 1 }, this.getAllBanners);
  };

  // Modal toggle handlers
  toggleToggleModal = () => this.setState({ showToggleModal: !this.state.showToggleModal });
  toggleDeleteModal = () => this.setState({ showDeleteModal: !this.state.showDeleteModal });

  handleToggleStatus = (id, currentStatus) => {
    this.setState({ selectedBannerId: id, selectedBannerStatus: currentStatus, showToggleModal: true });
  };

  confirmToggleStatus = () => {
    const { selectedBannerId, selectedBannerStatus } = this.state;
    const action = selectedBannerStatus ? "deactivate" : "activate";
    this.setState({ actionLoading: true });
    toggleBannerStatus(selectedBannerId)
      .then((response) => {
        if (response.status === 200) {
          toast.success(`Banner ${action}d successfully`);
          this.getAllBanners();
        } else {
          toast.error(response.message || `Failed to ${action} banner`);
        }
      })
      .catch(() => toast.error(`Error ${action}ing banner`))
      .finally(() => {
        this.setState({ actionLoading: false, showToggleModal: false, selectedBannerId: null, selectedBannerStatus: null });
      });
  };

  handleDelete = (id) => {
    this.setState({ selectedBannerId: id, showDeleteModal: true });
  };

  confirmDelete = () => {
    const { selectedBannerId } = this.state;
    this.setState({ actionLoading: true });
    deleteBanner(selectedBannerId)
      .then((response) => {
        if (response.status === 200) {
          toast.success("Banner deleted successfully");
          this.getAllBanners();
          // Also refresh analytics data
          this.loadAnalytics();
        } else {
          toast.error(response.message || "Failed to delete banner");
        }
      })
      .catch(() => toast.error("Error deleting banner"))
      .finally(() => {
        this.setState({ actionLoading: false, showDeleteModal: false, selectedBannerId: null });
      });
  };

  toggleAnalyticsModal = () => {
    const { analyticsModal } = this.state;
    if (!analyticsModal) {
      this.loadAnalytics();
    }
    this.setState({ analyticsModal: !analyticsModal });
  };

  loadAnalytics = () => {
    getBannerAnalytics()
      .then((response) => {
        if (response.status === 200) {
          this.setState({ analyticsData: response.data });
        } else {
          toast.error("Failed to load analytics");
        }
      })
      .catch(() => toast.error("Error loading analytics"));
  };

  // --- Reordering ------------------------------------------------------------
  // The design's "Reorder" affordance. Drag-and-drop would need a dependency
  // this project does not have, so the same intent is served by move up/down
  // and one explicit save through the existing reorder endpoint.
  startReorder = () => this.setState({ reordering: true, order: this.state.items.map((b) => b._id) });

  cancelReorder = () => this.setState({ reordering: false, order: this.state.items.map((b) => b._id) });

  move = (id, delta) => {
    const order = [...this.state.order];
    const i = order.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= order.length) return;
    order[i] = order[j];
    order[j] = id;
    this.setState({ order });
  };

  saveOrder = () => {
    this.setState({ savingOrder: true });
    reorderBanners(this.state.order)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Banner order saved");
          this.getAllBanners();
        } else {
          toast.error((response && response.message) || "Failed to save order");
        }
      })
      .catch(() => toast.error("Error saving banner order"))
      .finally(() => this.setState({ savingOrder: false }));
  };

  orderedItems() {
    const { items, order, reordering } = this.state;
    if (!reordering) return items;
    const byId = {};
    items.forEach((b) => { byId[b._id] = b; });
    return order.map((id) => byId[id]).filter(Boolean);
  }

  selectedBanner = () => this.state.items.find((b) => b._id === this.state.selectedBannerId) || null;

  renderCard(banner, index) {
    const state = lifecycle(banner);
    const write = canWrite();
    const { reordering, order } = this.state;

    return (
      <div key={banner._id} className="banner-card">
        {/* The artwork fills the card's full height, whatever the copy beside
            it grows to — a letterboxed strip reads as a broken image. */}
        <div style={{ flex: "0 0 220px", background: C.iconTint, position: "relative", minHeight: 168 }}>
          <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}>
            <Thumb src={getImageUrl(banner.imageUrl)} alt={banner.title} height="100%" />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: "18px 20px", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                padding: "5px 11px", borderRadius: 10, fontSize: 12, fontWeight: 700,
                background: state.bg, color: state.fg,
              }}
            >
              {state.label}
            </span>
            <span style={{ fontSize: 12, color: C.taupeLight, letterSpacing: ".5px" }}>
              SLOT {banner.displayOrder + 1} · {(banner.placement || "home").toUpperCase()}
            </span>
            {reordering && write && (
              <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button
                  type="button"
                  className="banner-move"
                  title="Move up"
                  disabled={order.indexOf(banner._id) === 0}
                  onClick={() => this.move(banner._id, -1)}
                >
                  <span className="ms" style={{ fontSize: 17 }}>arrow_upward</span>
                </button>
                <button
                  type="button"
                  className="banner-move"
                  title="Move down"
                  disabled={order.indexOf(banner._id) === order.length - 1}
                  onClick={() => this.move(banner._id, 1)}
                >
                  <span className="ms" style={{ fontSize: 17 }}>arrow_downward</span>
                </button>
              </span>
            )}
          </div>

          <div style={{ marginTop: 10, fontSize: 18, fontWeight: 500, color: C.ink }}>{banner.title}</div>
          <div style={{ marginTop: 6, fontSize: 13, color: C.taupe }}>{scheduleLine(banner)}</div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.taupeLight }}>{linkLine(banner)}</div>

          <div style={{ marginTop: 10, display: "flex", gap: 18, fontSize: 13, color: C.taupe }}>
            <span>{(banner.impressionCount || 0).toLocaleString()} views</span>
            {banner.linkType !== "none" && (
              <span>{(banner.clickCount || 0).toLocaleString()} taps</span>
            )}
          </div>

          {write && !reordering && (
            <div style={{ marginTop: "auto", paddingTop: 14, display: "flex", alignItems: "center", gap: 16 }}>
              <button
                type="button"
                className="banner-act"
                onClick={() => this.props.history.push(`/banner/edit/${banner._id}`)}
              >
                Edit
              </button>
              <button
                type="button"
                className="banner-act"
                onClick={() => this.handleToggleStatus(banner._id, banner.isActive)}
              >
                {banner.isActive ? "Pause" : "Resume"}
              </button>
              <button
                type="button"
                className="banner-act is-danger"
                onClick={() => this.handleDelete(banner._id)}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderPager() {
    const { pagination, currentPage } = this.state;
    if (!pagination || pagination.totalPages <= 1) return null;

    const total = pagination.totalPages;
    const pages = Array.from({ length: Math.min(5, total) }, (_, i) => {
      if (total <= 5) return i + 1;
      if (pagination.currentPage <= 3) return i + 1;
      if (pagination.currentPage >= total - 2) return total - 4 + i;
      return pagination.currentPage - 2 + i;
    });

    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontSize: 13, color: C.taupe }}>
          Page {pagination.currentPage} of {total} · {pagination.totalCount} banner
          {pagination.totalCount === 1 ? "" : "s"}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="banner-page"
            disabled={!pagination.hasPrevPage}
            onClick={() => this.handlePageChange(currentPage - 1)}
          >
            ‹
          </button>
          {pages.map((p) => (
            <button
              key={p}
              type="button"
              className={`banner-page${p === pagination.currentPage ? " is-on" : ""}`}
              onClick={() => this.handlePageChange(p)}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            className="banner-page"
            disabled={!pagination.hasNextPage}
            onClick={() => this.handlePageChange(currentPage + 1)}
          >
            ›
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { isLoaded, statusFilter, analyticsData, searchQuery, reordering, savingOrder } = this.state;
    const write = canWrite();
    const list = this.orderedItems();
    const selected = this.selectedBanner();

    // Carousel preview — the first banner a guest would actually see.
    const liveOnes = list.filter((b) => lifecycle(b).label === "LIVE");
    const hero = liveOnes[0] || list[0] || null;

    // Taps per banner, from the analytics endpoint this page already calls.
    // The handoff draws a seven-day series; no daily history is exposed, so
    // this charts totals instead of inventing days.
    const taps = ((analyticsData && analyticsData.banners) || [])
      .filter((b) => (b.clickCount || 0) > 0)
      .sort((a, b) => (b.clickCount || 0) - (a.clickCount || 0))
      .slice(0, 7)
      .map((b) => ({ label: b.title, value: b.clickCount || 0 }));

    return (
      <ConsolePage
        title="Guest app banners"
        subtitle={
          isLoaded
            ? `Marketing / Banners · ${(this.state.pagination && this.state.pagination.totalCount) || list.length} total`
            : "Loading banners…"
        }
        actions={
          <React.Fragment>
            <SearchPill
              value={searchQuery}
              onChange={this.handleSearchChange}
              placeholder="Search banners"
              width={220}
            />
            <Btn kind="quiet" icon="bar_chart" onClick={this.toggleAnalyticsModal}>
              Analytics
            </Btn>
            {write && !reordering && list.length > 1 && (
              <Btn kind="quiet" icon="swap_vert" onClick={this.startReorder}>
                Reorder
              </Btn>
            )}
            {write && reordering && (
              <React.Fragment>
                <Btn kind="quiet" onClick={this.cancelReorder} disabled={savingOrder}>
                  Cancel
                </Btn>
                <Btn icon="check" onClick={this.saveOrder} disabled={savingOrder}>
                  {savingOrder ? "Saving…" : "Save order"}
                </Btn>
              </React.Fragment>
            )}
            {write && !reordering && (
              <Btn icon="add" onClick={() => this.props.history.push("/banner/create")}>
                New banner
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {analyticsData && (
            <StatRow>
              <StatCard icon="image" value={analyticsData.overall.totalBanners} label="Banners" />
              <StatCard icon="visibility" value={analyticsData.overall.activeBanners} label="Active banners" />
              <StatCard
                icon="ads_click"
                value={(analyticsData.overall.totalImpressions || 0).toLocaleString()}
                label="Total views"
              />
              <StatCard icon="percent" value={`${analyticsData.overall.overallCTR}%`} label="Overall tap rate" />
            </StatRow>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", gap: 20, alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <ChipTabs
                  value={statusFilter}
                  onChange={this.setStatusFilter}
                  options={[
                    { value: "", label: "All banners" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                />
                <select
                  className="banner-limit"
                  value={this.state.limit}
                  onChange={this.handleLimitChange}
                  aria-label="Banners per page"
                  style={{ marginLeft: "auto" }}
                >
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
              </div>

              {reordering && (
                <div
                  style={{
                    padding: "12px 16px", borderRadius: 14, background: C.iconTint,
                    fontSize: 13, color: C.brown,
                  }}
                >
                  Reordering this page. Slot 1 shows first in the app carousel.
                  Nothing changes until you press Save order.
                </div>
              )}

              {!isLoaded ? (
                <div style={{ padding: "60px 0", textAlign: "center", color: C.taupeLight }}>
                  Loading banners…
                </div>
              ) : list.length === 0 ? (
                <Panel>
                  <div style={{ padding: "40px 0", textAlign: "center" }}>
                    <span className="ms" style={{ fontSize: 34, color: C.sand }}>image</span>
                    <div style={{ marginTop: 12, fontSize: 16, color: C.ink }}>No banners here</div>
                    <div style={{ marginTop: 6, fontSize: 14, color: C.taupe }}>
                      {searchQuery || statusFilter
                        ? "Nothing matches this search."
                        : "Banners fill the carousel on the guest app's home screen."}
                    </div>
                    {write && !searchQuery && !statusFilter && (
                      <div style={{ marginTop: 18, display: "flex", justifyContent: "center" }}>
                        <Btn icon="add" onClick={() => this.props.history.push("/banner/create")}>
                          Add the first banner
                        </Btn>
                      </div>
                    )}
                  </div>
                </Panel>
              ) : (
                <React.Fragment>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {list.map((b, i) => this.renderCard(b, i))}
                  </div>
                  {this.renderPager()}
                </React.Fragment>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Panel title="Carousel preview" style={{ padding: 20 }}>
                <div style={{ marginTop: 14, borderRadius: 20, background: C.brownDeep, padding: "14px 12px 18px" }}>
                  <div style={{ borderRadius: 14, overflow: "hidden", height: 120 }}>
                    <Thumb src={hero ? getImageUrl(hero.imageUrl) : null} alt={hero ? hero.title : ""} height={120} />
                  </div>
                  <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 6 }}>
                    {(liveOnes.length ? liveOnes : list).slice(0, 5).map((b, i) => (
                      <span
                        key={b._id}
                        style={{
                          width: i === 0 ? 18 : 5, height: 5, borderRadius: 3,
                          background: i === 0 ? "#fff" : "rgba(255,255,255,.4)",
                        }}
                      />
                    ))}
                    {list.length === 0 && (
                      <span style={{ width: 18, height: 5, borderRadius: 3, background: "rgba(255,255,255,.4)" }} />
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 12, fontSize: 13, color: C.taupe }}>
                  {hero
                    ? `First card: ${hero.title}`
                    : "Nothing is live, so the carousel is empty in the app."}
                </div>
              </Panel>

              <Panel title="Taps by banner" subtitle="All time — the API keeps no daily history" style={{ padding: 20 }}>
                <div style={{ marginTop: 14 }}>
                  <BarChart data={taps} height={130} empty="No banner has been tapped yet" />
                </div>
              </Panel>

              <Panel title="Artwork specs" style={{ padding: 20 }}>
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 9, fontSize: 13, color: C.taupe }}>
                  <div>1200 × 400 px · JPG, PNG or WebP · under 5 MB</div>
                  <div>Keep text in the middle 70% — edges crop on small phones</div>
                  <div>Slot order decides the carousel order; slot 1 shows first</div>
                </div>
              </Panel>
            </div>
          </div>
        </div>

        {/* Analytics */}
        <Dialog isOpen={this.state.analyticsModal} toggle={this.toggleAnalyticsModal} size="lg">
          <DialogHead
            title="Banner analytics"
            subtitle="Views, taps and tap-through rate for every banner"
            onClose={this.toggleAnalyticsModal}
          />
          {analyticsData ? (
            <React.Fragment>
              <div style={{ marginTop: 22, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
                {[
                  ["Banners", analyticsData.overall.totalBanners],
                  ["Active", analyticsData.overall.activeBanners],
                  ["Total taps", (analyticsData.overall.totalClicks || 0).toLocaleString()],
                  ["Tap rate", `${analyticsData.overall.overallCTR}%`],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: "#fff", border: `1px solid ${C.tint}`, borderRadius: 14, padding: 16 }}>
                    <div style={{ fontSize: 24, fontWeight: 500, color: C.ink }}>{value}</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: C.taupe }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 22, background: "#fff", border: `1px solid ${C.tint}`, borderRadius: 16, padding: "6px 12px" }}>
                <Table
                  rowKey="_id"
                  columns={[
                    {
                      key: "title",
                      label: "Banner",
                      render: (b) => (
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 46, height: 28, borderRadius: 7, overflow: "hidden", flex: "0 0 46px" }}>
                            <Thumb src={getImageUrl(b.imageUrl)} alt="" height={28} width={46} />
                          </div>
                          <span>{b.title}</span>
                        </div>
                      ),
                    },
                    { key: "impressionCount", label: "Views", align: "right", render: (b) => (b.impressionCount || 0).toLocaleString() },
                    { key: "clickCount", label: "Taps", align: "right", render: (b) => (b.clickCount || 0).toLocaleString() },
                    { key: "ctr", label: "Tap rate", align: "right", render: (b) => `${b.ctr}%` },
                  ]}
                  rows={analyticsData.banners || []}
                  empty="No banner data yet"
                />
              </div>
            </React.Fragment>
          ) : (
            <div style={{ padding: "50px 0", textAlign: "center", color: C.taupeLight }}>
              Loading analytics…
            </div>
          )}
        </Dialog>

        {/* Toggle Status Confirmation */}
        <ConfirmDialog
          isOpen={this.state.showToggleModal}
          toggle={this.toggleToggleModal}
          onConfirm={this.confirmToggleStatus}
          tone="neutral"
          icon={this.state.selectedBannerStatus ? "pause" : "play_arrow"}
          title={
            this.state.selectedBannerStatus
              ? `Pause “${selected ? selected.title : "this banner"}”?`
              : `Show “${selected ? selected.title : "this banner"}” again?`
          }
          body={
            this.state.selectedBannerStatus
              ? "It disappears from the app carousel immediately. Its slot and artwork are kept."
              : "It returns to the carousel in its existing slot, within its own schedule."
          }
          confirmText={this.state.selectedBannerStatus ? "Pause banner" : "Show banner"}
          loading={this.state.actionLoading}
        />

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={this.state.showDeleteModal}
          toggle={this.toggleDeleteModal}
          onConfirm={this.confirmDelete}
          icon="delete"
          title={`Delete “${selected ? selected.title : "this banner"}”?`}
          body={
            selected && (selected.impressionCount || 0) > 0
              ? `This banner has been seen ${selected.impressionCount.toLocaleString()} times. Deleting removes it from the app and from this list.`
              : "This removes the banner from the guest app and from this list."
          }
          tip="Pausing keeps the artwork and slot for later. Deleting cannot be undone."
          confirmText="Delete"
          altText={selected && selected.isActive ? "Pause instead" : undefined}
          onAlt={
            selected && selected.isActive
              ? () => {
                  this.setState({ showDeleteModal: false });
                  this.handleToggleStatus(selected._id, true);
                }
              : undefined
          }
          loading={this.state.actionLoading}
        />
      </ConsolePage>
    );
  }
}

export default BannerList;
